class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.insuranceStorageKey = 'cart-insurance-opt-out-v1';
    this.cartMutationQueue = Promise.resolve();
    this.insuranceTimer = null;
    this.cartUpdateUnsubscriber = undefined;

    this.addEventListener('keydown', (event) => {
      if (event.code !== 'Escape') return;
      if (this.querySelector('.cart-addon-dialog[open]')) {
        event.stopPropagation();
        return;
      }
      this.close();
    });
    this.addEventListener('click', this.onDrawerClick.bind(this));
    this.addEventListener('change', this.onDrawerChange.bind(this));

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && (event.source === 'cart-addon' || event.source === 'cart-insurance')) return;
        this.scheduleInsuranceReconcile(250);
      });
    }
    this.syncInsuranceUI();
    this.scheduleInsuranceReconcile(350);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.insuranceTimer) clearTimeout(this.insuranceTimer);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => this.classList.add('animate', 'active'));

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    this.syncInsuranceUI();
    this.scheduleInsuranceReconcile(100);
  }

  close() {
    const openDialog = this.querySelector('.cart-addon-dialog[open]');
    if (openDialog) openDialog.close();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    if (!parsedState || !parsedState.sections) return;
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHTML !== null) sectionElement.innerHTML = innerHTML;
    });

    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    this.syncInsuranceUI();
    if (!options.skipInsuranceSchedule) this.scheduleInsuranceReconcile(250);

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      if (options.open !== false) this.open();
      if (options.focusProductId) {
        const card = this.querySelector(`[data-cart-addon-card][data-product-id="${options.focusProductId}"]`);
        const button = card && card.querySelector('[data-cart-addon-action]');
        if (button) button.focus();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  onDrawerClick(event) {
    const button = event.target.closest('[data-cart-addon-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.cartAddonAction;
    if (action === 'options') {
      const dialog = document.getElementById(button.getAttribute('aria-controls'));
      if (!dialog || typeof dialog.showModal !== 'function') return;
      dialog.showModal();
      const select = dialog.querySelector('[data-cart-addon-variant]');
      if (select) select.focus();
      dialog.addEventListener('close', () => button.focus(), { once: true });
      return;
    }

    if (action === 'confirm') {
      const dialog = button.closest('.cart-addon-dialog');
      const select = dialog && dialog.querySelector('[data-cart-addon-variant]');
      if (!select || !select.value) return;
      this.updateAddon(button, {
        action: 'add',
        variantId: select.value,
        productId: button.dataset.productId,
        dialog
      });
      return;
    }

    const card = button.closest('[data-cart-addon-card]');
    this.updateAddon(button, {
      action,
      variantId: button.dataset.variantId,
      lineKey: button.dataset.lineKey,
      productId: card && card.dataset.productId
    });
  }

  onDrawerChange(event) {
    const toggle = event.target.closest('[data-cart-insurance-toggle]');
    if (!toggle || !this.contains(toggle)) return;
    this.updateInsurancePreference(toggle);
  }

  updateAddon(button, details) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    this.setButtonLoading(button, true);
    this.clearAddonError(button);

    this.enqueueCartMutation(async () => {
      let response;
      if (details.action === 'remove') {
        response = await this.requestCartMutation(this.getCartApiUrl('change'), {
          id: details.lineKey,
          quantity: 0
        }, 'cart-addon', { render: false });
      } else {
        response = await this.requestCartMutation(this.getCartApiUrl('add'), {
          items: [{
            id: Number(details.variantId),
            quantity: 1,
            properties: { __addon: 'custom_addon' }
          }]
        }, 'cart-addon', { render: false });
      }

      if (details.dialog && details.dialog.open) details.dialog.close();
      this.renderContents(response, { open: false, focusProductId: details.productId });
    }).catch((error) => {
      console.error('Cart add-on update failed:', error);
      this.showAddonError(button);
      this.setButtonLoading(button, false);
    });
  }

  setButtonLoading(button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-disabled', String(isLoading));
  }

  clearAddonError(button) {
    const container = button.closest('.cart-addon-dialog') || button.closest('[data-cart-addon-card]');
    const error = container && container.querySelector('.cart-addon-dialog__error, .cart-addon__error');
    if (!error) return;
    error.hidden = true;
    error.textContent = '';
  }

  showAddonError(button) {
    const container = button.closest('.cart-addon-dialog') || button.closest('[data-cart-addon-card]');
    const error = container && container.querySelector('.cart-addon-dialog__error, .cart-addon__error');
    if (!error) return;
    error.textContent = window.cartAddonStrings.updateError;
    error.hidden = false;
  }

  enqueueCartMutation(task) {
    const run = this.cartMutationQueue.catch(() => undefined).then(task);
    this.cartMutationQueue = run.catch(() => undefined);
    return run;
  }

  async requestCartMutation(url, payload, source, options = {}) {
    const body = {
      ...payload,
      sections: this.getSectionsToRender().map((section) => section.id).join(','),
      sections_url: window.location.pathname
    };
    const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
    const data = await this.parseCartResponse(response);
    const statusCode = Number(data.status);
    const hasErrorStatus = Number.isFinite(statusCode) && statusCode >= 400;
    const hasErrors = Array.isArray(data.errors)
      ? data.errors.length > 0
      : Boolean(data.errors && (typeof data.errors !== 'object' || Object.keys(data.errors).length));
    if (!response.ok || hasErrorStatus || hasErrors) {
      throw new Error(this.getCartErrorMessage(data));
    }
    if (options.render !== false) {
      this.renderContents(data, { open: false, skipInsuranceSchedule: options.skipInsuranceSchedule === true });
    }
    this.publishCartUpdate(source);
    return data;
  }

  getCartApiUrl(action) {
    const routeKey = action ? `cart_${action}_url` : 'cart_url';
    const fallbackRoute = window.routes && window.routes[routeKey];
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;

    if (root) {
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      return `${normalizedRoot}cart${action ? `/${action}` : ''}.js`;
    }
    if (fallbackRoute) return fallbackRoute.endsWith('.js') ? fallbackRoute : `${fallbackRoute}.js`;
    return `/cart${action ? `/${action}` : ''}.js`;
  }

  async parseCartResponse(response) {
    const responseText = await response.text();
    if (!responseText) return {};

    try {
      return JSON.parse(responseText);
    } catch (error) {
      throw new Error(`${window.cartStrings.error} (${response.status})`);
    }
  }

  getCartErrorMessage(data) {
    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.errors === 'string' && data.errors) return data.errors;
    if (Array.isArray(data.errors) && data.errors.length) return data.errors.join(' ');
    if (data.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat().filter(Boolean);
      if (messages.length) return messages.join(' ');
    }
    return window.cartStrings.error;
  }

  publishCartUpdate(source, productVariantId) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId });
  }

  scheduleInsuranceReconcile(delay = 0) {
    if (this.dataset.insuranceEnabled !== 'true' && this.dataset.insuranceInCart !== 'true') return;
    if (this.insuranceTimer) clearTimeout(this.insuranceTimer);
    this.insuranceTimer = setTimeout(() => {
      this.insuranceTimer = null;
      this.reconcileInsurance();
    }, delay);
  }

  reconcileInsurance() {
    this.clearInsuranceError();
    return this.enqueueCartMutation(async () => {
      const cart = await this.fetchCart();
      await this.reconcileInsuranceState(cart);
      this.syncInsuranceUI();
    }).catch((error) => {
      console.error('Shipping insurance update failed:', error);
      this.showInsuranceError(error);
    });
  }

  async reconcileInsuranceState(cart) {
    const enabled = this.dataset.insuranceEnabled === 'true';
    const variantId = Number(this.dataset.insuranceVariantId || 0);
    const visibleItems = cart.items.filter((item) => !item.properties || item.properties.__addon !== 'insurance');
    let insuranceItem = cart.items.find((item) => item.properties && item.properties.__addon === 'insurance');

    if (!enabled || !variantId || visibleItems.length === 0) {
      if (insuranceItem) {
        await this.requestCartMutation(this.getCartApiUrl('change'), {
          id: insuranceItem.key,
          quantity: 0
        }, 'cart-insurance', { skipInsuranceSchedule: true });
      }
      this.dataset.insuranceInCart = 'false';
      return;
    }

    const optedOut = this.getInsuranceOptOut(cart.token);
    if (optedOut) {
      if (insuranceItem) {
        await this.requestCartMutation(this.getCartApiUrl('change'), {
          id: insuranceItem.key,
          quantity: 0
        }, 'cart-insurance', { skipInsuranceSchedule: true });
      }
      this.dataset.insuranceInCart = 'false';
      return;
    }

    if (insuranceItem && Number(insuranceItem.variant_id) !== variantId) {
      const updatedCart = await this.requestCartMutation(this.getCartApiUrl('change'), {
        id: insuranceItem.key,
        quantity: 0
      }, 'cart-insurance', { skipInsuranceSchedule: true });
      insuranceItem = updatedCart.items && updatedCart.items.find((item) => item.properties && item.properties.__addon === 'insurance');
    }

    if (!insuranceItem) {
      await this.requestCartMutation(this.getCartApiUrl('add'), {
        items: [{
          id: variantId,
          quantity: 1,
          properties: { __addon: 'insurance' }
        }]
      }, 'cart-insurance', { skipInsuranceSchedule: true });
      this.dataset.insuranceInCart = 'true';
      return;
    }

    this.dataset.insuranceInCart = 'true';
    if (insuranceItem.quantity !== 1) {
      await this.requestCartMutation(this.getCartApiUrl('change'), {
        id: insuranceItem.key,
        quantity: 1
      }, 'cart-insurance', { skipInsuranceSchedule: true });
    }
  }

  updateInsurancePreference(toggle) {
    const optedOut = !toggle.checked;
    const previousOptOut = !optedOut;
    toggle.disabled = true;
    this.clearInsuranceError();
    this.setInsuranceOptOut(optedOut, this.currentCartToken || '');
    this.syncInsuranceUI();

    this.enqueueCartMutation(async () => {
      const cart = await this.fetchCart();
      this.setInsuranceOptOut(optedOut, cart.token || '');
      await this.reconcileInsuranceState(cart);
      this.syncInsuranceUI();
    }).catch((error) => {
      console.error('Shipping insurance preference failed:', error);
      this.setInsuranceOptOut(previousOptOut, this.currentCartToken || '');
      this.syncInsuranceUI();
      this.showInsuranceError(error);
    }).finally(() => {
      const currentToggle = this.querySelector('[data-cart-insurance-toggle]');
      if (currentToggle) currentToggle.disabled = false;
    });
  }

  async fetchCart() {
    const response = await fetch(this.getCartApiUrl(), {
      headers: { Accept: 'application/json' }
    });
    const cart = await this.parseCartResponse(response);
    if (!response.ok) throw new Error(this.getCartErrorMessage(cart));
    this.currentCartToken = cart.token || '';
    return cart;
  }

  getInsurancePreference() {
    try {
      return JSON.parse(localStorage.getItem(this.insuranceStorageKey) || '{}');
    } catch (error) {
      return {};
    }
  }

  getInsuranceOptOut(cartToken) {
    const preference = this.getInsurancePreference();
    const defaultOptOut = this.dataset.insuranceDefaultEnabled !== 'true';
    if (typeof preference.optOut !== 'boolean') return defaultOptOut;
    if (cartToken && preference.cartToken && preference.cartToken !== cartToken) {
      localStorage.removeItem(this.insuranceStorageKey);
      return defaultOptOut;
    }
    if (cartToken && !preference.cartToken) this.setInsuranceOptOut(preference.optOut, cartToken);
    return preference.optOut;
  }

  setInsuranceOptOut(optedOut, cartToken) {
    try {
      localStorage.setItem(this.insuranceStorageKey, JSON.stringify({
        optOut: optedOut,
        cartToken: cartToken || ''
      }));
    } catch (error) {
      console.error('Unable to save shipping insurance preference:', error);
    }
  }

  syncInsuranceUI() {
    const optedOut = this.getInsuranceOptOut(this.currentCartToken || '');
    const toggle = this.querySelector('[data-cart-insurance-toggle]');
    const subtotalWithInsurance = this.querySelector('[data-cart-subtotal-with-insurance]');
    const subtotalWithoutInsurance = this.querySelector('[data-cart-subtotal-without-insurance]');

    if (toggle) toggle.checked = !optedOut;
    if (subtotalWithInsurance) subtotalWithInsurance.hidden = optedOut;
    if (subtotalWithoutInsurance) subtotalWithoutInsurance.hidden = !optedOut;
    document.body.classList.toggle('cart-insurance-opted-out', optedOut);
  }

  clearInsuranceError() {
    const errorElement = this.querySelector('[data-cart-insurance-error]');
    if (!errorElement) return;
    errorElement.textContent = '';
    errorElement.hidden = true;
  }

  showInsuranceError(error) {
    const errorElement = this.querySelector('[data-cart-insurance-error]');
    if (!errorElement) return;
    errorElement.textContent = error && error.message ? error.message : window.cartStrings.error;
    errorElement.hidden = false;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.ensureInsuranceLast = function () {
  const drawer = document.querySelector('cart-drawer');
  return drawer ? drawer.reconcileInsurance() : Promise.resolve();
};

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceItems = html.querySelector('cart-drawer-items');
        if (sourceItems) this.innerHTML = sourceItems.innerHTML;
      })
      .catch((error) => {
        console.error('Cart drawer refresh error:', error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
